import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { Material } from '../../../../classes/material.class';
import { Planning } from '../../../../classes/planning.class';
import { CodaltComponent } from '../../../../codalt.component';
import { FilterByEntityTypePipe } from '../../../../pipes/filter-by-entity-type.pipe';
import { PlanningService } from '../../../../services/planning/planning.service';
import { TracklinePlanningEditDialogComponent } from '../../trackline-planning-edit-dialog/trackline-planning-edit-dialog.component';
import { PlanningHighlightService } from '../../trackline-planning/planning-highlight.service';
import { FilterMenuOption } from '../filter-menu/filter-menu.component';
import { MapFilter, PlanningMapComponent } from '../track-line-week/planning-map/planning-map.component';
import { BEHOEFTE_COLUMNS, columnMatchesFilters, getNeedColumnKeys } from './behoefte-column.util';

type BehoefteColumn = { key: string; label: string; needs: Planning[] };

const MATERIAL_TYPES = ['thermoplast', 'verf', 'koudplast'];
const MARKING_KINDS = ['streep', 'figuratie', 'onbekend'];

const MATERIAL_TYPE_LABELS: Record<string, string> = {
    thermoplast: 'Thermoplast',
    verf: 'Verf',
    koudplast: 'Koudplast',
};

const MARKING_KIND_FILTER_LABELS: Record<string, string> = {
    streep: 'Strepen / lengtemarkering',
    figuratie: 'Figuratie',
    onbekend: 'Onbekend',
};

const toOptions = (values: string[], labels: Record<string, string>): FilterMenuOption[] =>
    values.map(value => ({ value, label: labels[value] ?? value }));

@Component({
    selector: 'app-behoefte-overview',
    standalone: false,
    templateUrl: './behoefte-overview.component.html',
    styleUrl: './behoefte-overview.component.scss',
})
export class BehoefteOverviewComponent extends CodaltComponent implements OnInit {
    @ViewChild('planningMap') planningMap: PlanningMapComponent;

    PlanningStatus = this.environment.status.Planning;

    entityTypeOptions: FilterMenuOption[] = environment.planningEntityTypes.map(entityType => ({
        value: entityType.key,
        label: entityType.name,
    }));
    materialTypeOptions = toOptions(MATERIAL_TYPES, MATERIAL_TYPE_LABELS);
    markingKindOptions = toOptions(MARKING_KINDS, MARKING_KIND_FILTER_LABELS);

    needList: Planning[] = [];
    materialsById: Map<number, Material> = new Map();
    columns: BehoefteColumn[] = [];
    filteredNeeds: Planning[] = [];
    hoveredNeedId: number | null = null;
    mapFilter: MapFilter = 'alle';

    fcFilteredEntityTypes = new FormControl(environment.planningEntityTypes.map(e => e.key));
    fcFilteredMaterialTypes = new FormControl(MATERIAL_TYPES);
    fcFilteredMarkingKind = new FormControl(MARKING_KINDS);
    fcSearchNeed = new FormControl<string>('');

    constructor(
        private planningService: PlanningService,
        private dialog: MatDialog,
        protected highlightService: PlanningHighlightService
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.planningService.materials$.subscribe(materials => {
                this.materialsById = new Map(materials.map(material => [material.id, material]));
                this.applyFilters();
            })
        );

        this.subscriptions.add(
            this.planningService.getListByStatus(this.PlanningStatus.PlanningBehoefte).subscribe(needs => {
                this.needList = needs;
                this.applyFilters();
            })
        );

        this.subscriptions.add(this.fcFilteredEntityTypes.valueChanges.subscribe(() => this.applyFilters()));
        this.subscriptions.add(this.fcFilteredMaterialTypes.valueChanges.subscribe(() => this.applyFilters()));
        this.subscriptions.add(this.fcFilteredMarkingKind.valueChanges.subscribe(() => this.applyFilters()));
        this.subscriptions.add(
            this.fcSearchNeed.valueChanges.pipe(debounceTime(300)).subscribe(() => this.applyFilters())
        );
    }

    applyFilters() {
        const entityFiltered = new FilterByEntityTypePipe().transform(
            this.needList,
            this.fcFilteredEntityTypes.value,
            this.fcSearchNeed.value,
            this.materialsById
        );

        const materialTypes = this.fcFilteredMaterialTypes.value ?? [];
        const markingKinds = this.fcFilteredMarkingKind.value ?? [];

        this.columns = BEHOEFTE_COLUMNS.filter(column => columnMatchesFilters(column.key, materialTypes, markingKinds))
            .map(column => ({
                ...column,
                needs: entityFiltered.filter(need => getNeedColumnKeys(need, this.materialsById).includes(column.key)),
            }))
            .filter(column => column.needs.length > 0);

        this.filteredNeeds = [...new Set(this.columns.flatMap(column => column.needs))];
    }

    onMapFilterChange(filter: MapFilter) {
        this.mapFilter = filter;
    }

    onPlanningHover(planning: Planning | null) {
        this.highlightService.setHovered(planning?.id ?? null);
        this.hoveredNeedId = planning?.id ?? null;
    }

    openPlanning(planning: Planning) {
        this.dialog.open(TracklinePlanningEditDialogComponent, {
            disableClose: true,
            panelClass: 'planning-edit-dialog',
            data: { planning },
        });
    }
}
